// Claim documents in the certificate pack (V0.4.12): the claim's own files (the contractor's claim PDF and any
// substantiating documents) that can be bundled right after the certificate page. Shared by the pack window and the
// functions so both pick the same default.
export type PackDocFile = { id: string; original_name: string; mime: string | null; uploaded_at?: string | null; in_pack?: boolean | null; response_id?: string | null };

/** PDFs and JPG/PNG photos can go in the one-PDF pack; Word / Excel etc. cannot. */
export const packable = (f: { mime: string | null; original_name: string }) =>
  /pdf/i.test(f.mime ?? '') || /image\/(jpeg|jpg|png)/i.test(f.mime ?? '') || (!f.mime && /\.(pdf|jpe?g|png)$/i.test(f.original_name));

/** The main claim document: the file Claude read for this claim (claim read, else the new-item read), else the first
 *  PDF named like a claim, else the earliest PDF. Null when the claim has no PDF. */
export function mainClaimDoc(files: PackDocFile[], readFileIds: string[] = []): string | null {
  const pdfs = files.filter((f) => /pdf/i.test(f.mime ?? '') || /\.pdf$/i.test(f.original_name));
  for (const id of readFileIds) if (files.some((f) => f.id === id)) return id;
  const byDate = [...pdfs].sort((a, b) => String(a.uploaded_at ?? '').localeCompare(String(b.uploaded_at ?? '')));
  return (byDate.find((f) => /claim/i.test(f.original_name)) ?? byDate[0])?.id ?? null;
}

/** Files offered under "Claim documents": current, packable, not already ticked as progress evidence. Oldest first. */
export const docChoices = <T extends PackDocFile>(files: T[]) =>
  files.filter((f) => packable(f) && !f.in_pack).sort((a, b) => String(a.uploaded_at ?? '').localeCompare(String(b.uploaded_at ?? '')));

/** ids from a query string ("a,b") or JSON array; null when not given (= use the default, the main claim document). */
export function docsFrom(v: unknown): string[] | null {
  if (v === null || v === undefined) return null;
  const list = Array.isArray(v) ? v.map(String) : String(v).split(',');
  const re = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return [...new Set(list.map((s) => s.trim()).filter((s) => re.test(s)))].slice(0, 30);
}
